import React from 'react';
import { useSettings } from '../../../contexts/SettingsContext';
import { useSound } from '../../../contexts/SoundContext';
import { Icon } from '../../Icons';
import { WALLPAPERS } from '../../../utils/constants';
import type { ThemeType } from '../../../types';
import styles from '../AppContent.module.css';
import localStyles from './Settings.module.css';

const THEMES: { id: ThemeType; name: string; colors: { primary: string; secondary: string; accent: string } }[] = [
  {
    id: 'luna-blue',
    name: 'Luna Blue',
    colors: { primary: '#0054E3', secondary: '#3168D5', accent: '#4E8FEA' },
  },
  {
    id: 'luna-silver',
    name: 'Luna Silver',
    colors: { primary: '#7A8A9C', secondary: '#9EAAB8', accent: '#B8C4D0' },
  },
  {
    id: 'luna-olive',
    name: 'Luna Olive',
    colors: { primary: '#6C8C4C', secondary: '#9CAC8C', accent: '#ACBC9C' },
  },
];

export const Settings: React.FC = () => {
  const { settings, toggleSound, toggleCRT, setWallpaper, setTheme } = useSettings();
  const { playSound } = useSound();

  const handleToggleSound = () => {
    toggleSound();
    playSound('click');
  };

  const handleToggleCRT = () => {
    toggleCRT();
    playSound('click');
  };

  const handleWallpaperChange = (url: string) => {
    setWallpaper(url);
    playSound('click');
  };

  const handleThemeChange = (theme: ThemeType) => {
    setTheme(theme);
    playSound('click');
  };

  return (
    <div className={styles.appContainer}>
      <div className={styles.contentPanel}>
        <div className={localStyles.header}>
          <Icon name="settings" size={48} />
          <div>
            <h1 className={localStyles.title}>Display Settings</h1>
            <p className={localStyles.subtitle}>Customize your Windows XP experience</p>
          </div>
        </div>

        <div className={localStyles.sections}>
          {/* Theme Settings */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14.5c-3.58 0-6.5-2.92-6.5-6.5S6.42 3.5 10 3.5s6.5 2.92 6.5 6.5-2.92 6.5-6.5 6.5z"/>
                <circle cx="10" cy="10" r="3"/>
              </svg>
              Color Theme
            </h2>
            <div className={localStyles.themes}>
              {THEMES.map((theme) => (
                <button
                  key={theme.id}
                  className={`${localStyles.themeOption} ${
                    settings.theme === theme.id ? localStyles.themeSelected : ''
                  }`}
                  onClick={() => handleThemeChange(theme.id)}
                >
                  <div className={localStyles.themePreview}>
                    <div
                      className={localStyles.themeColor}
                      style={{ background: theme.colors.primary }}
                    />
                    <div
                      className={localStyles.themeColor}
                      style={{ background: theme.colors.secondary }}
                    />
                    <div
                      className={localStyles.themeColor}
                      style={{ background: theme.colors.accent }}
                    />
                  </div>
                  <span className={localStyles.themeName}>{theme.name}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Sound Settings */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 7 L2 13 L6 13 L12 18 L12 2 L6 7 Z"/>
                <path d="M15 6 C17 8 17 12 15 14" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Sound
            </h2>
            <div className={localStyles.setting}>
              <div className={localStyles.settingInfo}>
                <span className={localStyles.settingLabel}>Sound Effects</span>
                <span className={localStyles.settingDescription}>
                  Play sounds for clicks, notifications, and system events
                </span>
              </div>
              <button
                className={`${localStyles.toggle} ${settings.soundEnabled ? localStyles.toggleOn : ''}`}
                onClick={handleToggleSound}
                role="switch"
                aria-checked={settings.soundEnabled}
              >
                <span className={localStyles.toggleSlider} />
                <span className={localStyles.toggleLabel}>
                  {settings.soundEnabled ? 'On' : 'Off'}
                </span>
              </button>
            </div>
          </section>

          {/* CRT Effect */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <rect x="2" y="3" width="16" height="12" rx="1" fill="none" stroke="currentColor" strokeWidth="2"/>
                <rect x="6" y="15" width="8" height="2"/>
                <rect x="4" y="17" width="12" height="1"/>
              </svg>
              Visual Effects
            </h2>
            <div className={localStyles.setting}>
              <div className={localStyles.settingInfo}>
                <span className={localStyles.settingLabel}>CRT Monitor Effect</span>
                <span className={localStyles.settingDescription}>
                  Add scanlines and slight curvature for retro monitor feel
                </span>
              </div>
              <button
                className={`${localStyles.toggle} ${settings.crtEffect ? localStyles.toggleOn : ''}`}
                onClick={handleToggleCRT}
                role="switch"
                aria-checked={settings.crtEffect}
              >
                <span className={localStyles.toggleSlider} />
                <span className={localStyles.toggleLabel}>
                  {settings.crtEffect ? 'On' : 'Off'}
                </span>
              </button>
            </div>
          </section>

          {/* Wallpaper */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <Icon name="computer" size={20} />
              Desktop Background
            </h2>
            <div className={localStyles.wallpapers}>
              {WALLPAPERS.map((wp) => (
                <button
                  key={wp.id}
                  className={`${localStyles.wallpaperOption} ${
                    settings.wallpaper === wp.url ? localStyles.wallpaperSelected : ''
                  }`}
                  onClick={() => handleWallpaperChange(wp.url)}
                >
                  <div
                    className={localStyles.wallpaperPreview}
                    style={{
                      background: wp.url === 'none' ? '#3A6EA5' : `url(${wp.url}) center/cover`,
                    }}
                  />
                  <span className={localStyles.wallpaperName}>{wp.name}</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className={localStyles.footer}>
          <p>Settings are automatically saved.</p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
